import { Link } from "react-router-dom";
import { Home as HomeIcon, Car, Wallet, Briefcase, CheckCircle2, Phone, ArrowRight } from "lucide-react";
import { BUSINESS } from "../lib/api";
import { SEO } from "../components/SEO";

const services = [
  {
    id: "home-loans",
    title: "Home Loans",
    icon: HomeIcon,
    tagline: "Buy, build, refinance or invest.",
    img: "https://images.unsplash.com/photo-1666000346172-8e7e87547d2a?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    body: "From first-home buyers to seasoned investors, we compare over 40 lenders to secure the sharpest rate and structure for your situation. We handle the paperwork, chase valuations, and stay on top of settlements — so you can focus on the exciting bit.",
    bullets: ["First home buyer & FHOG applications", "Refinances & cash-out", "Investment loans & portfolio structuring", "Construction & bridging loans"],
  },
  {
    id: "car-loans",
    title: "Car & Asset Finance",
    icon: Car,
    tagline: "New, used, business or personal.",
    img: "https://images.unsplash.com/photo-1774740770881-fba338a79565?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    body: "Whether it’s the family SUV, your first ute, or a fleet upgrade — we access lenders you don’t see on comparison sites, often with same-day approvals and pre-approvals to strengthen your bargaining power.",
    bullets: ["Consumer & business car loans", "Chattel mortgages & novated leases", "Equipment & machinery finance", "Same-day pre-approvals available"],
  },
  {
    id: "personal-loans",
    title: "Personal Loans",
    icon: Wallet,
    tagline: "Renovations, weddings, consolidation.",
    img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    body: "Simple, transparent lending for life’s bigger moments. We’ll benchmark unsecured and secured options, explain the true cost, and only recommend a loan that genuinely improves your financial position.",
    bullets: ["Home renovations & extensions", "Debt consolidation", "Weddings, travel & major purchases", "Fixed or variable structures"],
  },
  {
    id: "business-loans",
    title: "Business & Commercial",
    icon: Briefcase,
    tagline: "Fuel your next chapter.",
    img: "https://images.unsplash.com/photo-1573164574572-cb89e39749b4?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    body: "Cash-flow lending, commercial property, equipment finance, low-doc lending for the self-employed. We work with brokers, accountants and specialist lenders to build a finance stack that supports growth without stress.",
    bullets: ["Commercial property loans", "Working capital & overdraft", "Low-doc & self-employed", "Development & construction finance"],
  },
];

export default function Services() {
  return (
    <div>
      <SEO
        path="/services"
        title="Home, Car, Personal & Business Finance"
        description="Sixty Finance services: home loans, car & asset finance, personal loans, and business & commercial lending. Access to 40+ Australian lenders."
        keywords="home loans, car finance, asset finance, personal loans, business loans, commercial finance broker Australia"
      />
      {/* Header */}
      <section className="bg-emerald-950 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-24">
          <p className="text-xs uppercase tracking-widest text-emerald-300 font-semibold">Our services</p>
          <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter max-w-3xl text-white">
            Finance solutions, built around <span className="text-emerald-300">you.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-emerald-100/85 text-base md:text-lg leading-relaxed">
            Every loan is a conversation, not a transaction. Explore how we
            help — then let’s find your best move.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 space-y-24">
        {services.map((s, idx) => {
          const Icon = s.icon;
          const reverse = idx % 2 === 1;
          return (
            <div
              id={s.id}
              key={s.id}
              data-testid={`service-section-${s.id}`}
              className={`grid md:grid-cols-2 gap-10 md:gap-16 items-center ${reverse ? "md:[&>div:first-child]:order-2" : ""}`}
            >
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-widest text-emerald-800 border border-emerald-100">
                  <Icon className="h-3.5 w-3.5" />
                  {s.title}
                </div>
                <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight text-emerald-950">
                  {s.tagline}
                </h2>
                <p className="mt-4 text-slate-600 leading-relaxed">{s.body}</p>
                <ul className="mt-6 space-y-2">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-emerald-950">
                      <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-500 flex-none" />
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/quote"
                    data-testid={`service-quote-${s.id}`}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-900 text-white font-semibold text-sm hover:bg-emerald-800 transition-colors"
                  >
                    Get a free quote <ArrowRight className="h-4 w-4" />
                  </Link>
                  <a
                    href={BUSINESS.phoneHref}
                    data-testid={`service-call-${s.id}`}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-emerald-200 text-emerald-900 font-semibold text-sm hover:bg-emerald-50 transition-colors"
                  >
                    <Phone className="h-4 w-4" />
                    <span className="num">{BUSINESS.phone}</span>
                  </a>
                </div>
              </div>
              <div className="rounded-3xl overflow-hidden shadow-xl relative">
                <img src={s.img} alt={s.title} className="w-full h-[380px] object-cover" />
                <div className="absolute inset-0 ring-1 ring-inset ring-emerald-950/10 rounded-3xl" />
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}
